import { useMemo } from "react"
import { useQuery } from "react-query"

import { getContactInvoiceStatuses } from "src/api/Billing/Payments"

import { backoffPolling } from "src/utils/backoff_polling"

// A Stripe payment or refund in flight resolves by webhook, with no one acting
// on the invoice. Every other status waits on staff, the autopay schedule, or a
// dispute that takes weeks, so a poll would only find it unchanged.
const PROCESSING = "Processing"
const IN_FLIGHT_STATUSES = new Set([PROCESSING])

const isInFlight = (invoice) => IN_FLIGHT_STATUSES.has(invoice.displayStatus)

// A card payment resolves in seconds and a bank debit in days, so the polls
// stop after about ten minutes instead of following a debit to the end. Unlike
// invoicesIncludeProcessingCardPayments (Billing/helpers.js), which skips bank
// debits, this polls them too: the 12-poll cap bounds them.
const POLLING = backoffPolling({
  shouldPoll: (statuses) => Boolean(statuses?.some(isInFlight)),
  firstDelay: 3000,
  maxDelay: 60000,
  maxPolls: 12,
})

const useInvoiceStatusPolling = ({
  marinaSlug,
  contactId,
  invoices,
  enabled,
  onStatusChange,
}) => {
  const inFlightIds = useMemo(
    () =>
      invoices
        .filter(isInFlight)
        .map((invoice) => invoice.id)
        .sort(),
    [invoices]
  )

  useQuery(
    ["contactInvoiceStatuses", marinaSlug, contactId, inFlightIds],
    () =>
      getContactInvoiceStatuses({ marinaSlug, contactId, ids: inFlightIds }),
    {
      enabled: enabled && inFlightIds.length > 0,
      // The rows were just loaded, so the first poll waits a full delay.
      initialData: () =>
        inFlightIds.map((id) => ({ id, displayStatus: PROCESSING })),
      staleTime: Infinity,
      // A row that resolves and goes back to Processing under the same ids
      // must start over, not read the cached final status.
      cacheTime: 0,
      retry: false,
      ...POLLING,
      onSuccess: (statuses) => {
        const stillInFlight = statuses.filter(isInFlight)
        if (stillInFlight.length < inFlightIds.length) onStatusChange()
      },
    }
  )
}

export default useInvoiceStatusPolling
